import { Constants } from "../models/Constants";
import { BaseMessageModel, ErrorModel } from "../models/ErrorModel";
import { deleteCall, getCall, postCall } from "./api";
import { instance } from "./instance";
import { notificationService } from "./notificationService";
import i18next from "../i18n";
import { StatusCode } from "../models/Enums";
import { DataAnalysisReport, DeleteReportFile, PublishAssessment, ShowAssessmentResultOnGlobalDashboard } from "../models/ReportModel/ReportModel";

/** Handles all responsibilities related to Report generate */
export const reportService = {


    /** Publish assessment
     * Uses instance.post() directly (bypasses postCall) so that we control the
     * error notification instead of postCall's catch handler firing a generic
     * "An error occurred" toast before the caller can do anything about it.
     * A 10-minute timeout is used because the SaveAssessmentHealthFacilityMapping
     * SP can be slow on large datasets in production.
     * request: an object of type PublishAssessment
     */
    publishAssessment: (request: PublishAssessment) =>
        new Promise<boolean>((resolve, reject) => {
            const url = `${Constants.Api.baseUrl}/${Constants.Api.Url.DATA_ANALYSIS_REPORT_PUBLISH_RESPONSE}`;
            instance.post(url, request, { timeout: 600000 } as any)
                .then((response: any) => {
                    const result = response.data;
                    if (result) {
                        notificationService.sendMessage(
                            new BaseMessageModel(
                                i18next.t("Report.Message.PublishSuccessMessage"),
                                StatusCode.Created
                            )
                        );
                    } else {
                        notificationService.sendMessage(
                            new BaseMessageModel(
                                i18next.t("Report.Message.PublishErrorMessage"),
                                StatusCode.PreConditionFailed
                            )
                        );
                    }
                    resolve(result);
                })
                .catch((error: any) => {
                    notificationService.sendMessage(
                        new BaseMessageModel(
                            i18next.t("Report.Message.PublishErrorMessage"),
                            StatusCode.PreConditionFailed
                        )
                    );
                    reject(error);
                });
        }),


    /** Opt out assessment on global dashboard
  * request: an object of type ShowAssessmentResultOnGlobalDashboard
  */
    optOutAssessmentOnGlobalDashboard: (request: ShowAssessmentResultOnGlobalDashboard) =>
        new Promise<boolean>((resolve, reject) => {
            postCall<ShowAssessmentResultOnGlobalDashboard>(
                Constants.Api.Url.DATA_ANALYSIS_SHOW_ASSESSMENT_ON_GLOBAL_DASHBOARD_RESPONSE,
                request
            ).then((response) => {
                resolve(response);
                if (response) {
                    notificationService.sendMessage(
                        new BaseMessageModel(
                            request.showResultOnGlobalDashboard ? i18next.t("Report.Message.OptOutSuccessMessage") : i18next.t("Report.Message.OptInSuccessMessage"),
                            StatusCode.Created
                        )
                    );
                } else {
                    notificationService.sendMessage(
                        new BaseMessageModel(
                            i18next.t("Report.Message.OptOutErrorMessage"),
                            StatusCode.PreConditionFailed
                        )
                    );
                }
            });
        }),

    /** Upload report file using background job pattern with polling.
     * request: FormData with AssessmentId and File
     * onProgress: optional callback for progress step updates
     * onAllComplete: optional callback when all steps are done
     */
    uploadFile: async (
        request: FormData,
        onProgress?: (step: string) => void,
        onAllComplete?: () => void
    ): Promise<boolean> => {
        const BACKEND_STEPS = [
            "",
            "Scanning for viruses...",
            "Uploading to storage...",
            "Saving to database...",
        ];
        const stepTranslations: Record<string, string> = {
            "Scanning for viruses...": i18next.t("Common.UploadStepScanning") as string || "Scanning for viruses...",
            "Uploading to storage...": i18next.t("Common.UploadStepUploadingStorage") as string || "Uploading to storage...",
            "Saving to database...":   i18next.t("Common.UploadStepSaving") as string || "Saving to database...",
        };

        onProgress?.(i18next.t("Common.Uploading") as string);
        const jobResponse: any = await postCall<FormData>(
            Constants.Api.Url.DATA_ANALYSIS_UPLOAD_FILE,
            request
        );

        const jobId: string = jobResponse?.jobId;
        if (!jobId) throw new Error(i18next.t("Errors.SomethingWentWrong") as string);

        const maxAttempts = 900;
        const pollIntervalMs = 2000;
        const stepVisibleMs = 1000;

        let lastShownIndex = 0;
        const animateToStep = async (targetIndex: number) => {
            for (let i = lastShownIndex + 1; i <= targetIndex; i++) {
                onProgress?.(stepTranslations[BACKEND_STEPS[i]] ?? BACKEND_STEPS[i]);
                await new Promise(resolve => setTimeout(resolve, stepVisibleMs));
            }
            lastShownIndex = Math.max(lastShownIndex, targetIndex);
        };

        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            await new Promise(resolve => setTimeout(resolve, pollIntervalMs));

            const statusUrl = `${Constants.Api.baseUrl}/${Constants.Api.Url.DATA_ANALYSIS_UPLOAD_STATUS.replace("{0}", jobId)}`;
            const statusResponse = await instance.get(statusUrl, { skipGlobalLoader: true } as any);
            const { status, currentStep, errorMessage } = statusResponse.data;

            if (currentStep) {
                const stepIndex = BACKEND_STEPS.indexOf(currentStep);
                if (stepIndex > lastShownIndex) await animateToStep(stepIndex);
            }

            if (status === "Succeeded") {
                await animateToStep(BACKEND_STEPS.length - 1);
                onAllComplete?.();
                await new Promise(resolve => setTimeout(resolve, 800));
                notificationService.sendMessage(new BaseMessageModel(
                    i18next.t("Report.Message.UploadFileSuccessMessage"),
                    StatusCode.Created
                ));
                return true;
            }

            if (status === "Failed") {
                const msg = errorMessage || i18next.t("Errors.SomethingWentWrong");
                notificationService.sendMessage(new BaseMessageModel(
                    i18next.t("Report.Message.UploadFileErrorMessage"),
                    StatusCode.PreConditionFailed
                ));
                throw new Error(msg as string);
            }
        }

        throw new Error("Data analysis report upload timed out after 30 minutes.");
    },

    /** Gets reports data selected by specific assessmentId 
* * @param assessmentId A string value
*/
    getReports: (assessmentId: string) =>
        new Promise<DataAnalysisReport>((resolve, reject) => {
            // call an api
            const url = Constants.Api.Url.DATA_ANALYSIS_REPORTS.replace(
                "{0}",
                assessmentId
            );

            getCall(url).then((response: any) => {
                if (response) {
                    resolve(response);
                } else {
                    resolve(DataAnalysisReport.init());
                }
            });
        }),

    /** Download report file by report id        
*  @param reportId A string value   
*/
    downloadReport: (reportId: string) =>
        new Promise<boolean>((resolve, reject) => {
            const url = Constants.Api.Url.DATA_ANALYSIS_DOWNLOAD_FILE.replace(
                "{0}",
                reportId
            );
            getCall(url, "blob").then((response: any) => {
                resolve(response);
            })
                .catch((error: ErrorModel) => {
                    if (error) {
                        notificationService.sendMessage(
                            new BaseMessageModel(
                                i18next.t("Errors.SomethingWentWrong"),
                                StatusCode.PreConditionFailed
                            )
                        );
                    }
                    reject(true);
                });
        }),

    /** Delete report file by report id        
    *  @param request: an object of type DeleteReportFile
    */
    deleteFile: (request: DeleteReportFile) =>
        new Promise<boolean>((resolve, reject) => {
            deleteCall<DeleteReportFile>(
                Constants.Api.Url.DATA_ANALYSIS_DELETE_FILE, request)
                .then((response: any) => {
                    if (response) {
                        notificationService.sendMessage(
                            new BaseMessageModel(
                                i18next.t("Report.Message.DeleteFileSuccessMessage"),
                                StatusCode.Created
                            )
                        );
                    } else {
                        notificationService.sendMessage(
                            new BaseMessageModel(
                                i18next.t("Report.Message.DeleteFileErrorMessage"),
                                StatusCode.PreConditionFailed
                            )
                        );
                    }
                    resolve(response);
                })
                .catch((error: ErrorModel) => {
                    if (error) {
                        notificationService.sendMessage(
                            new BaseMessageModel(
                                i18next.t("Errors.SomethingWentWrong"),
                                StatusCode.PreConditionFailed
                            )
                        );
                    }
                    reject(true);
                });
        }),
};